import { useState } from "react";
import { Link } from "react-router-dom";
import PermissionNotice from "@/components/PermissionNotice";
import { useAuth } from "@/hooks/useAuth";
import { useCaseList } from "@/hooks/useCases";
import {
  useCreateSample,
  useDeleteSample,
  useSampleList,
} from "@/hooks/useSamples";
import type { SampleCreate, SampleType } from "@/types";
import {
  sampleStatusColor,
  sampleStatusLabels,
  sampleTypeLabels,
} from "@/utils/labels";

const INITIAL_FORM: SampleCreate = {
  sample_id: "",
  name: "",
  sample_type: "gcms",
  case_id: 0,
};

export default function SamplesPage() {
  const { permissions } = useAuth();
  const { data: samples, isLoading, error } = useSampleList();
  const { data: cases } = useCaseList();
  const createSample = useCreateSample();
  const deleteSample = useDeleteSample();

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<SampleCreate>(INITIAL_FORM);
  const [caseSearch, setCaseSearch] = useState("");

  const filteredCases = cases?.filter(
    (c) =>
      caseSearch === "" ||
      c.title.toLowerCase().includes(caseSearch.toLowerCase()) ||
      c.case_number.toLowerCase().includes(caseSearch.toLowerCase()),
  );

  const handleCreate = async () => {
    if (!form.sample_id.trim() || !form.name.trim() || !form.case_id) {
      alert("시료 ID, 이름, 사건은 필수 입력 항목입니다.");
      return;
    }
    try {
      await createSample.mutateAsync(form);
      setForm(INITIAL_FORM);
      setCaseSearch("");
      setShowForm(false);
    } catch {
      alert("시료 등록에 실패했습니다.");
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("정말 이 시료를 삭제하시겠습니까?")) return;
    try {
      await deleteSample.mutateAsync(id);
    } catch {
      alert("삭제에 실패했습니다.");
    }
  };

  const selectedCase = cases?.find((c) => c.id === form.case_id);

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">시료 관리</h1>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 animate-pulse">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-12 bg-gray-200 rounded mb-3" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">시료 관리</h1>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <p className="text-red-600 font-medium">
            데이터를 불러오는 중 오류가 발생했습니다.
          </p>
          <p className="text-red-500 text-sm mt-1">
            {(error as Error).message}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">시료 관리</h1>
        <p className="text-gray-600">시료를 등록하고 관리할 수 있습니다.</p>
      </div>

      {/* 새 시료 등록 */}
      {permissions?.canCreateSamples ? (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">
            신규 시료 등록
          </h2>
          {!showForm && (
            <button
              type="button"
              onClick={() => setShowForm(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              + 새 시료 등록
            </button>
          )}
        </div>

        {showForm ? (
          <div className="border border-gray-200 rounded-lg p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block">
                  <span className="block text-sm font-medium text-gray-700 mb-1">
                    시료 ID *
                  </span>
                  <input
                    type="text"
                    value={form.sample_id}
                    onChange={(e) =>
                      setForm({ ...form, sample_id: e.target.value })
                    }
                    placeholder="예: SAMPLE-2025-001"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </label>
              </div>
              <div>
                <label className="block">
                  <span className="block text-sm font-medium text-gray-700 mb-1">
                    이름 *
                  </span>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="시료 이름"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </label>
              </div>
              <div>
                <label className="block">
                  <span className="block text-sm font-medium text-gray-700 mb-1">
                    시료 유형
                  </span>
                  <select
                    value={form.sample_type}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        sample_type: e.target.value as SampleType,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="gcms">GC-MS</option>
                    <option value="ftir">FTIR</option>
                    <option value="mixed">혼합</option>
                  </select>
                </label>
              </div>
              <div>
                {/* 사건 선택 영역은 <label>로 감싸지 않는다 — 감싸면 제안 버튼을 누르는
                    순간 label 이 그 클릭을 새로 생긴 [✕] 버튼으로 다시 보내 선택이
                    스스로 풀린다(2026-09-16 브라우저 실측). */}
                <div>
                  <span className="block text-sm font-medium text-gray-700 mb-1">
                    사건 선택 *
                  </span>
                  {/* A2: 사건 선택 드롭다운 (검색 가능) */}
                  {selectedCase ? (
                    <div className="flex items-center space-x-2">
                      <div className="flex-1 px-3 py-2 border border-blue-300 bg-blue-50 rounded-md text-sm">
                        <span className="font-medium">{selectedCase.case_number}</span>
                        {" — "}
                        {selectedCase.title}
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setForm({ ...form, case_id: 0 });
                          setCaseSearch("");
                        }}
                        className="px-2 py-2 text-gray-500 hover:text-gray-700"
                      >
                        ✕
                      </button>
                    </div>
                  ) : (
                    <div className="relative">
                      <input
                        type="text"
                        value={caseSearch}
                        onChange={(e) => setCaseSearch(e.target.value)}
                        placeholder="사건번호 또는 제목으로 검색..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      {caseSearch && filteredCases && filteredCases.length > 0 && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-y-auto">
                          {filteredCases.map((c) => (
                            <button
                              key={c.id}
                              type="button"
                              onClick={() => {
                                setForm({ ...form, case_id: c.id });
                                setCaseSearch("");
                              }}
                              className="w-full text-left px-3 py-2 hover:bg-blue-50 text-sm border-b border-gray-100 last:border-b-0"
                            >
                              <span className="font-medium text-gray-900">{c.case_number}</span>
                              {" — "}
                              <span className="text-gray-600">{c.title}</span>
                            </button>
                          ))}
                        </div>
                      )}
                      {caseSearch && filteredCases && filteredCases.length === 0 && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg p-3 text-sm text-gray-500">
                          일치하는 사건이 없습니다.
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="flex justify-end space-x-4 mt-6">
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setForm(INITIAL_FORM);
                  setCaseSearch("");
                }}
                disabled={createSample.isPending}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors"
              >
                취소
              </button>
              <button
                type="button"
                onClick={handleCreate}
                disabled={createSample.isPending}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                {createSample.isPending ? "등록 중..." : "시료 등록"}
              </button>
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            새로운 시료를 등록하려면 "새 시료 등록" 버튼을 클릭하세요.
          </div>
        )}
      </div>
      ) : (
        <PermissionNotice action="시료 등록" requiredRole="분석관" />
      )}

      {/* 시료 목록 */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">시료 목록</h2>
          <p className="text-sm text-gray-500 mt-1">
            총 {samples?.length ?? 0}개의 시료
          </p>
        </div>

        {samples && samples.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    시료 ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    이름
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    유형
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    상태
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    사건 ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    등록일
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    작업
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {samples.map((sample) => (
                  <tr key={sample.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Link
                        to={`/samples/${sample.id}`}
                        className="text-sm font-medium text-blue-600 hover:text-blue-800 cursor-pointer"
                      >
                        {sample.sample_id}
                      </Link>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <Link
                        to={`/samples/${sample.id}`}
                        className="text-blue-600 hover:text-blue-800 cursor-pointer"
                      >
                        {sample.name}
                      </Link>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {sampleTypeLabels[sample.sample_type] ??
                        sample.sample_type}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${sampleStatusColor(sample.status)}`}
                      >
                        {sampleStatusLabels[sample.status] ?? sample.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {sample.case_id != null ? (
                        <Link
                          to={`/cases/${sample.case_id}`}
                          className="text-blue-600 hover:text-blue-800 cursor-pointer"
                        >
                          {sample.case_id}
                        </Link>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(sample.created_at).toLocaleDateString("ko-KR")}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {permissions?.canDeleteSamples && (
                      <button
                        type="button"
                        onClick={() => handleDelete(sample.id)}
                        disabled={deleteSample.isPending}
                        className="text-red-600 hover:text-red-900 font-medium disabled:opacity-50 cursor-pointer"
                      >
                        삭제
                      </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-12 text-center">
            <p className="text-lg font-medium text-gray-900 mb-2">
              등록된 시료가 없습니다
            </p>
            <p className="text-gray-500">
              위의 "새 시료 등록" 버튼으로 시료를 추가하세요.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
